import React from 'react';
import { useTheme, usePreset, useAssetUrl } from '../../lib/ThemeContext';
import { useChatStore } from '../../stores/chatStore';

const DEFAULT_AVATAR_URL = 'https://i.pravatar.cc/150?img=12';

interface ChatHeaderProps {
  onCapsuleClick?: () => void;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({ onCapsuleClick }) => {
  const { onMinimize, headerText, headerSubtitle, avatarUrl, theme, onDragStart, t } = useTheme();
  const preset = usePreset();
  const assetUrl = useAssetUrl();
  const { chatTitle } = useChatStore();

  const isNv = preset === 'nv';
  const isNevi = preset === 'nevi';
  const assistantName = headerText || t.chatHeader.defaultName;
  const companyName = headerSubtitle || t.chatHeader.defaultSubtitle;
  const avatarUrlToUse =
    avatarUrl ||
    (isNv ? assetUrl('/widget/nv-avatar.webp') : isNevi ? assetUrl('/widget/nevi-avatar.webp') : DEFAULT_AVATAR_URL);
  const headerStyle = theme?.headerStyle || 'glass';
  const glassOpacity = theme?.headerGlassOpacity ?? 0.12;
  const isFlat = headerStyle === 'flat';

  if (isNevi) {
    // NeVi: dark glass island header — Figma node 1905:18604
    return (
      <div
        className="wc-relative"
        onMouseDown={onDragStart}
        style={{ cursor: onDragStart ? 'grab' : undefined, zIndex: 4, padding: '14px' }}
      >
        {/* Close button — glass square, Figma node 1905:18603 */}
        {onMinimize && (
          <button
            onClick={onMinimize}
            className="wc-absolute wc-flex wc-items-center wc-justify-center focus:wc-outline-none"
            style={{
              width: '32px',
              height: '32px',
              right: '14px',
              top: '14px',
              background: 'rgba(251, 251, 251, 0.05)',
              border: '1px solid rgba(251, 251, 251, 0.15)',
              borderRadius: '6px',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(251, 251, 251, 0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(251, 251, 251, 0.05)';
            }}
            aria-label={t.chatHeader.minimizeChat}
          >
            {/* Close remove - Iconly Pro */}
            <svg viewBox="0 0 11.6671 11.6668" fill="none" style={{ width: '13px', height: '13px' }}>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.01187 5.8335L11.4227 1.42271C11.7485 1.09688 11.7485 0.570208 11.4227 0.244375C11.0969 -0.0814583 10.5702 -0.0814583 10.2444 0.244375L5.83353 4.65517L1.42271 0.244375C1.09687 -0.0814583 0.570208 -0.0814583 0.244375 0.244375C-0.0814583 0.570208 -0.0814583 1.09688 0.244375 1.42271L4.6552 5.8335L0.244375 10.2444C-0.0814583 10.5702 -0.0814583 11.0968 0.244375 11.4227C0.406875 11.5852 0.620208 11.6668 0.833541 11.6668C1.04687 11.6668 1.26021 11.5852 1.42271 11.4227L5.83353 7.01192L10.2444 11.4227C10.4069 11.5852 10.6202 11.6668 10.8335 11.6668C11.0469 11.6668 11.2602 11.5852 11.4227 11.4227C11.7485 11.0968 11.7485 10.5702 11.4227 10.2444L7.01187 5.8335Z"
                fill="#FBFBFB"
              />
            </svg>
          </button>
        )}

        {/* Header island — Figma: padding 4px 12px 4px 4px, gap 16px, border-radius 12px */}
        <div
          className="wc-flex wc-items-center wc-relative"
          style={{
            padding: '4px 12px 4px 4px',
            gap: '16px',
            background: 'rgba(251, 251, 251, 0.05)',
            border: '1px solid rgba(251, 251, 251, 0.15)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '12px',
            width: 'fit-content',
            transition: 'background 0.15s ease',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(251, 251, 251, 0.12)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(251, 251, 251, 0.05)';
          }}
          onClick={(e) => {
            e.stopPropagation();
            onCapsuleClick?.();
          }}
        >
          {/* Avatar 40px, radius 8 */}
          <div
            className="wc-flex-shrink-0"
            style={{ width: '40px', height: '40px', borderRadius: '8px', overflow: 'hidden' }}
          >
            <img
              src={avatarUrlToUse}
              alt={assistantName}
              style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover' }}
            />
          </div>

          {/* Online indicator — Figma node 1905:18609 */}
          <div
            className="wc-absolute"
            style={{
              width: '8px',
              height: '8px',
              left: '38px',
              top: '37px',
              background: '#2EE181',
              borderRadius: '96px',
            }}
          />

          {/* Text */}
          <div className="wc-flex wc-flex-col wc-justify-center wc-min-w-0">
            <span
              style={{
                fontFamily: "'WC-Satoshi', system-ui, sans-serif",
                fontWeight: 700,
                fontSize: '18px',
                lineHeight: '24px',
                color: '#FBFBFB',
              }}
            >
              {assistantName}
            </span>
            {(chatTitle || companyName) && (
              <span
                style={{
                  fontFamily: "'WC-Satoshi', system-ui, sans-serif",
                  fontWeight: 300,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#F9AA66',
                }}
                title={chatTitle || companyName}
              >
                {chatTitle || companyName}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isNv) {
    return (
      <div
        className="wc-relative"
        onMouseDown={onDragStart}
        style={{ cursor: onDragStart ? 'grab' : undefined, zIndex: 4, padding: '16px' }}
      >
        {/* Close button — Figma: right:16 top:16 from widget edge */}
        {onMinimize && (
          <button
            onClick={onMinimize}
            className="wc-absolute wc-flex wc-items-center wc-justify-center focus:wc-outline-none"
            style={{
              width: '32px',
              height: '32px',
              right: '14px',
              top: '14px',
              background: '#FFFFFF',
              border: '1px solid rgba(46, 46, 46, 0.15)',
              borderRadius: '100px',
              transition: 'opacity 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
            aria-label={t.chatHeader.minimizeChat}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              style={{ width: '16px', height: '16px', color: '#2E2E2E' }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Header capsule — Figma: padding 4px 32px 4px 4px, gap 12px, border-radius 64px */}
        <div
          className="wc-flex wc-items-center wc-relative"
          style={{
            padding: '4px 32px 4px 4px',
            gap: '12px',
            background: '#FFFFFF',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '64px',
            width: 'fit-content',
            transition: 'background 0.15s ease',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#FFFFFF';
          }}
          onClick={(e) => {
            e.stopPropagation();
            onCapsuleClick?.();
          }}
        >
          {/* Avatar 56px */}
          <div
            className="wc-flex-shrink-0"
            style={{ width: '56px', height: '56px', borderRadius: '96px', overflow: 'hidden' }}
          >
            <img
              src={avatarUrlToUse}
              alt={assistantName}
              style={{ width: '56px', height: '56px', borderRadius: '96px', objectFit: 'cover' }}
            />
          </div>

          {/* Online indicator — positioned from capsule */}
          <div
            className="wc-absolute"
            style={{
              width: '8px',
              height: '8px',
              left: '47px',
              top: '47px',
              background: '#2EE181',
              borderRadius: '96px',
            }}
          />

          {/* Text */}
          <div className="wc-flex wc-flex-col wc-justify-center wc-min-w-0">
            <span
              style={{
                fontFamily: "'WC-Involve', sans-serif",
                fontWeight: 600,
                fontSize: '18px',
                lineHeight: '24px',
                color: '#2E2E2E',
              }}
            >
              {assistantName}
            </span>
            {(chatTitle || companyName) && (
              <span
                style={{
                  fontFamily: "'WC-Involve', sans-serif",
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: 'rgba(46, 46, 46, 0.7)',
                }}
                title={chatTitle || companyName}
              >
                {chatTitle || companyName}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="wc-relative wc-overflow-hidden"
      onMouseDown={onDragStart}
      style={{ cursor: onDragStart ? 'grab' : undefined }}
    >
      {/* Background layer */}
      {isFlat ? (
        <div
          className="wc-absolute wc-inset-0"
          style={{
            backgroundColor: 'var(--wc-header-background-color)',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          }}
        />
      ) : (
        <>
          <div
            className="wc-absolute wc-inset-0"
            style={{
              background: `linear-gradient(180deg, rgba(255, 255, 255, ${glassOpacity}) 0%, rgba(255, 255, 255, ${glassOpacity * 0.33}) 50%, rgba(255, 255, 255, 0) 100%)`,
              backdropFilter: 'blur(40px) saturate(180%)',
              WebkitBackdropFilter: 'blur(40px) saturate(180%)',
            }}
          />
          <div
            className="wc-absolute wc-top-0 wc-left-0 wc-right-0 wc-h-[1px]"
            style={{
              background: `linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, ${glassOpacity * 2.5}) 20%, rgba(255, 255, 255, ${glassOpacity * 4}) 50%, rgba(255, 255, 255, ${glassOpacity * 2.5}) 80%, transparent 100%)`,
            }}
          />
          <div
            className="wc-absolute wc-bottom-0 wc-left-0 wc-right-0 wc-h-[1px]"
            style={{
              background: `linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, ${glassOpacity * 0.8}) 30%, rgba(255, 255, 255, ${glassOpacity * 1.25}) 50%, rgba(255, 255, 255, ${glassOpacity * 0.8}) 70%, transparent 100%)`,
            }}
          />
        </>
      )}

      {/* Content — single flex row: avatar, text, close button */}
      <div className="wc-relative wc-flex wc-items-center wc-gap-3 wc-px-4 wc-py-3">
        {/* Avatar */}
        <div className="wc-relative wc-flex-shrink-0">
          {!isFlat && (
            <div
              className="wc-absolute wc-inset-[-2px] wc-rounded-full"
              style={{
                background:
                  'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.2) 100%)',
                padding: '1px',
              }}
            >
              <div className="wc-w-full wc-h-full wc-rounded-full wc-bg-black/20" />
            </div>
          )}
          <img
            src={avatarUrlToUse}
            alt={assistantName}
            className="wc-relative wc-w-9 wc-h-9 wc-rounded-full wc-object-cover"
            style={{ boxShadow: isFlat ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.3)' }}
          />
        </div>

        {/* Text */}
        <div
          className="wc-flex wc-flex-col wc-justify-center wc-min-w-0 wc-flex-1"
          style={{ color: 'var(--wc-header-color)' }}
        >
          <span
            className="wc-text-sm wc-font-semibold wc-leading-tight"
            style={{ textShadow: isFlat ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.3)' }}
          >
            {assistantName}
          </span>
          {(chatTitle || companyName) && (
            <span
              className="wc-text-xs wc-leading-tight wc-truncate wc-mt-0.5"
              style={{
                opacity: isFlat ? 0.8 : 0.6,
                textShadow: isFlat ? 'none' : '0 1px 1px rgba(0, 0, 0, 0.2)',
              }}
              title={chatTitle || companyName}
            >
              {chatTitle || companyName}
            </span>
          )}
        </div>

        {/* Close button — in flex flow, vertically centered */}
        {onMinimize && (
          <button
            onClick={onMinimize}
            className="wc-flex-shrink-0 wc-flex wc-items-center wc-justify-center wc-w-8 wc-h-8 wc-rounded-lg wc-border-none focus:wc-outline-none wc-transition-colors wc-duration-150"
            style={
              isFlat
                ? { background: 'rgba(255, 255, 255, 0.15)' }
                : {
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
                    boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.2), 0 1px 3px rgba(0, 0, 0, 0.2)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                  }
            }
            aria-label={t.chatHeader.minimizeChat}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="wc-w-4 wc-h-4"
              style={{ color: 'var(--wc-header-color)', opacity: isFlat ? 0.9 : 0.7 }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};
